import type { GetWhatsNewQuery } from "~/gql/queries/whatsNew.generated";

import { Button } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import logger from "~/helpers/logger";
import userAppSettingBlob from "~/helpers/userAppSettingBlob";
import useUserAppSettingMutations from "~/hooks/mutations/useUserAppSettingMutations";
import { useAppSelector } from "~/hooks/reduxHooks";
import useFeatureFlag from "~/hooks/useFeatureFlag";
import useLaunchTour from "~/hooks/useLaunchTour";
import whatsNew, {
	type WhatsNewItem,
	type WhatsNewState,
	type WhatsNewTab,
	type WhatsNewTabSlug
} from "~/pages/home/helpers/whatsNew";
import whatsNewReload from "~/pages/home/helpers/whatsNewReload";
import { useSnacks } from "~/providers/SnackProvider";
import { useGetUserAppSettingsQuery, useGetWhatsNewQuery } from "~/services/endpoints/apiEndpoints";

export interface UseWhatsNewHomeReturn {
	canLaunchTour: (tourId?: string | null) => boolean;
	launchTour: (item: WhatsNewItem) => void;
	close: () => void;
	dismiss: () => Promise<unknown>;
	dismissed: boolean;
	isOpen: boolean;
	item?: WhatsNewItem;
	latest?: GetWhatsNewQuery["whatsNew"]["releases"][number];
	loadFailed: boolean;
	open: () => void;
	openTab: (slug: WhatsNewTabSlug) => void;
	pulse: boolean;
	releaseNotesUrl?: string | null;
	retry: () => void;
	select: (key: string, releaseLabel?: string) => void;
	settingsLoaded: boolean;
	tab?: WhatsNewTab;
	tabs: WhatsNewTab[];
	visible: boolean;
}

const useWhatsNewState = (): UseWhatsNewHomeReturn => {
	const { t } = useTranslation("common");
	const [openSnack, closeSnack] = useSnacks();
	const navigate = useNavigate();
	const { isEnabled } = useFeatureFlag(FeatureFlagName.WhatsNew);
	const tenantId = useAppSelector((state) => state.settings.tenantId);
	const email = useAppSelector((state) => state.settings.email);
	const {
		data: whatsNewResponse,
		error: feedError,
		isError: feedFailed,
		isSuccess: feedLoaded,
		refetch: refetchFeed
	} = useGetWhatsNewQuery(undefined, { skip: !isEnabled, refetchOnFocus: true });
	const tabs = useMemo(
		() => (whatsNewResponse ? whatsNew.buildTabs(whatsNewResponse.whatsNew, tenantId) : []),
		[whatsNewResponse, tenantId]
	);
	const visible = isEnabled && tabs.length > 0;
	const {
		data: settings,
		error: settingsError,
		isError: settingsFailed,
		isSuccess: settingsLoaded,
		refetch: refetchSettings
	} = useGetUserAppSettingsQuery(
		{ first: 1, where: { emailAddress: { eq: email }, group: { eq: "home" }, name: { eq: "whatsNew" } } },
		{ skip: !visible || !email, refetchOnFocus: true }
	);
	const loadFailed = isEnabled && ((feedFailed && !whatsNewResponse) || (visible && settingsFailed && !settings));
	useEffect(() => {
		if (isEnabled && feedFailed) logger.error("Failed to load What's new", feedError);
	}, [isEnabled, feedFailed, feedError]);
	useEffect(() => {
		if (visible && settingsFailed) logger.error("Failed to load What's new settings", settingsError);
	}, [visible, settingsFailed, settingsError]);
	const { updateUserAppSetting } = useUserAppSettingMutations();
	const settingsBlob = settings?.userAppSettings?.nodes?.[0]?.base64Settings;
	const whatsNewSettings = useMemo(() => userAppSettingBlob.decode<WhatsNewState>(settingsBlob, {}), [settingsBlob]);
	const [params, setParams] = useSearchParams();
	const remembered = useRef<Partial<Record<WhatsNewTabSlug, { key: string; releaseLabel?: string }>>>({});
	const tab = tabs.find((tab) => tab.slug === params.get("whatsNew")) ?? tabs[0];
	const item =
		tab?.items.find(
			(item) =>
				item.feature.key === params.get("item") &&
				(!params.has("release") || item.releaseLabel === params.get("release"))
		) ?? tab?.items[0];
	const isOpen = !loadFailed && visible && params.has("whatsNew") && Boolean(item);
	const latest = whatsNewResponse?.whatsNew.releases[0];
	const releaseNotesUrl =
		tab?.slug === "past"
			? whatsNewResponse?.whatsNew.releases.find((release) => release.label === item?.releaseLabel)?.releaseNotesUrl
			: latest?.releaseNotesUrl;
	const pendingSeen = useRef<string | null>(null);
	const savedSettings = useRef(whatsNewSettings);
	const savedPatches = useRef<Partial<WhatsNewState>>({});
	useEffect(() => {
		savedSettings.current = { ...whatsNewSettings, ...savedPatches.current };
	}, [whatsNewSettings]);
	const saveQueue = useRef(Promise.resolve<unknown>(null));
	const save = useCallback(
		(patch: Partial<WhatsNewState>, silent = false) => {
			if (!email || !settingsLoaded) return Promise.resolve(null);
			const nextSave = saveQueue.current.then(async () => {
				const merged = { ...savedSettings.current, ...patch };
				const result = await updateUserAppSetting(
					{
						base64Settings: userAppSettingBlob.encode(merged),
						emailAddress: email,
						group: "home",
						name: "whatsNew"
					},
					{ silent }
				);
				if (result) {
					savedPatches.current = { ...savedPatches.current, ...patch };
					savedSettings.current = merged;
				}
				return result;
			});
			saveQueue.current = nextSave.catch(() => null);
			return nextSave;
		},
		[email, settingsLoaded, updateUserAppSetting]
	);
	const seenLabel = whatsNewSettings.seenLabel;
	const label = latest?.label;
	useEffect(() => {
		if (!isOpen || !label || !settingsLoaded || seenLabel === label || pendingSeen.current === label) return;
		pendingSeen.current = label;
		void save({ seenLabel: label }, true).then((savedSetting) => {
			if (!savedSetting) pendingSeen.current = null;
		});
	}, [isOpen, label, settingsLoaded, seenLabel, save]);
	const openTab = useCallback(
		(slug: WhatsNewTabSlug) => {
			const target = tabs.find((tab) => tab.slug === slug);
			if (!target) return;
			if (tab && item) remembered.current[tab.slug] = { key: item.feature.key, releaseLabel: item.releaseLabel };
			const previous = remembered.current[slug];
			const selection =
				target.items.find(
					(item) => item.feature.key === previous?.key && item.releaseLabel === previous?.releaseLabel
				) ?? target.items[0];
			navigate(whatsNew.buildSelectionPath(target, selection.feature.key, selection.releaseLabel));
		},
		[tabs, tab, item, navigate]
	);
	const open = useCallback(() => {
		if (tabs[0]) openTab(tabs[0].slug);
	}, [tabs, openTab]);
	const close = () =>
		setParams((current) => {
			const next = new URLSearchParams(current);
			next.delete("whatsNew");
			next.delete("item");
			next.delete("release");
			return next;
		});
	const select = (key: string, releaseLabel?: string) => {
		if (!tab) return;
		remembered.current[tab.slug] = { key, releaseLabel };
		setParams((current) => whatsNew.buildSelectionParams(current, tab, key, releaseLabel), { replace: true });
	};
	const tourIds = visible
		? tabs.flatMap((tab) => tab.items.flatMap((item) => (item.feature.tourId ? [item.feature.tourId] : [])))
		: [];
	const tourLauncher = useLaunchTour(tourIds);
	const pendingToast = useRef<string | null>(null);
	useEffect(() => {
		if (
			!visible ||
			!label ||
			!settingsLoaded ||
			!email ||
			whatsNewSettings.toastLabel === label ||
			pendingToast.current === label
		)
			return;
		pendingToast.current = label;
		openSnack(t("home.whatsNew.updated"), "info", false, (key) => (
			<Button
				color="inherit"
				onClick={() => {
					open();
					if (key !== undefined) closeSnack(key);
				}}
			>
				{t("home.whatsNew.seeWhatsNew")}
			</Button>
		));
		void save({ toastLabel: label }, true);
	}, [visible, label, settingsLoaded, email, whatsNewSettings.toastLabel, save, openSnack, closeSnack, t, open]);
	const pendingReload = useRef(false);
	const reloadCaptured = useRef(false);
	useEffect(() => {
		if (reloadCaptured.current) return;
		reloadCaptured.current = true;
		pendingReload.current = whatsNewReload.isPending();
		whatsNewReload.clear();
	}, []);
	useEffect(() => {
		if (!pendingReload.current || (!feedLoaded && !feedFailed)) return;
		pendingReload.current = false;
		if (visible) open();
	}, [feedLoaded, feedFailed, visible, open]);
	return {
		canLaunchTour: tourLauncher.canLaunch,
		launchTour: (item) => {
			if (!item.feature.tourId || !tourLauncher.canLaunch(item.feature.tourId)) return;
			tourLauncher.launch(item.feature.tourId, item.feature.id);
			void save({ touredLabel: label }, true);
		},
		close,
		dismiss: () => save({ dismissedLabel: label }),
		dismissed: whatsNewSettings.dismissedLabel === label,
		isOpen,
		item,
		latest,
		loadFailed,
		open,
		openTab,
		pulse: Boolean(label && whatsNew.shouldPulse(label, whatsNewSettings)),
		releaseNotesUrl,
		retry: () => {
			if (feedFailed) void refetchFeed();
			if (visible && settingsFailed) void refetchSettings();
		},
		select,
		settingsLoaded: settingsLoaded && Boolean(email),
		tab,
		tabs,
		visible
	};
};

export default useWhatsNewState;
